import { IMappedTaskData } from 'features/HorizonScanning/api/type-def';

import apiWrapper from '../../../../common/api/api-wrapper';
import { IAPIErrorContent } from '../../../../common/api/type-def';
import { CONTENT_TYPE } from '../../../../common/constants';
import { ENTITY_TYPE } from '../../../../common/constants/TaxonomyConstants';

import {
	ICreateUpdateTask,
	IImpactEntityData,
	IDerivedObligationRequestParams,
	IDerivedObligationSuccessResponse,
	IEditDerivedObligationRequestParams,
	IEditDerivedObligationSuccessResponse,
	IGraph,
	IImpactRequestParams,
	IRenderTreeErrorResponse,
	IRenderTreeSuccessResponse,
	IGraphDetails,
	IGraphPanel,
	IRSSNewsErrorResponse,
	IRSSNews,
	IProcessServiceData,
	IAddRelationshipMappingMenu,
	IAddRelationMappingMenuErrorResponse,
	IRiskServiceData,
	IEntityServiceData,
	IControlServiceData,
	IEntityDetailsStatus,
	IControlsResponse,
	IObligationsInp,
	ITaskCreateComment,
	ITaskComment,
	ICIPDetailsResponse,
	ITaskDetails,
	IImpactRequestParamForRegInv,
	ICoveredProductRes,
	ITaxonomyAddMappingParam,
	IObligationResponse,
	IGetEntityDetails,
	IGetRelationshipTaskData,
	ISaveComments,
	IUpdateImpact,
	IGetRelationMappingDetail,
	IGetRelationMappingParams,
	IObligationAddMappingParam,
	IRelationMappingPayload,
	IGetRelationMappingWithPagination,
	ISubmitPRCEditChanges,
	IRenegerateChanges,
} from './type-def';

interface ICitationInput {
	relationId: string;
	relationType: string;
}

interface IEntityRelationInput {
	relationId: string;
	relationType: string;
}

export interface IRelationTreeInput {
	regulationId: string | number;
}

const REG_CHANGE_DOMAIN_SERVICE_URL =
	process.env.REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL;
const REG_INV_URL = process.env.REACT_APP_REG_INVENTORY_DOMAIN_SERVICE_URL;
const { REACT_APP_RSS_FEED_SERVICE_URL } = process.env;

/** The below APIs are used for Task Creation journey using CIP.
 * These APIs are called when environment variable(REACT_APP_IS_CIP) is set to true.
 * Otherwise our RRCP platform API service is used when environment variable(REACT_APP_IS_CIP) is set to false
 */
const CIP_HANDLER_RAER_INVOKE_SERVICE =
	process.env.REACT_APP_CIP_SERVICE_HANDLER_RAER;

const OCP_APIM_SUBSCRIPTION_KEY =
	process.env.REACT_APP_OCP_APIM_SUBSCRIPTION_KEY;

const config: any = {
	headers: {
		'Ocp-Apim-Subscription-Key': OCP_APIM_SUBSCRIPTION_KEY,
	},
};

export const getCitationDetailsUsingCIP = async (citationId: string) => {
	const data = {
		citationId,
		source: 'rrcp-task-creation-source',
	};
	const resp = await apiWrapper.post<any>(
		`${CIP_HANDLER_RAER_INVOKE_SERVICE}`,
		data,
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const getRegChangeCitationDetailsUsingCIP = async (
	citationId: string,
	entityUpdateId: string,
) => {
	const data = {
		citationId,
		citationUpdateId: entityUpdateId,
		source: 'rrcp-task-creation-source',
	};
	const resp = await apiWrapper.post<any>(
		`${CIP_HANDLER_RAER_INVOKE_SERVICE}`,
		data,
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const getCitationRelationDataUsingCIP = async (
	params: ICitationInput,
) => {
	const data = {
		citationId: params.relationId,
		source: 'RRCP-EGETCITATIONRELATIONSHIP',
		request: params,
	};
	const resp = await apiWrapper.post<any>(
		`${CIP_HANDLER_RAER_INVOKE_SERVICE}`,
		data,
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const createImpactDataUsingCIP = async (
	request: IImpactRequestParams,
	citationId: string,
) => {
	const data = {
		citationId,
		source: 'rrcp-eImpactCreate',
		request,
	};
	const resp = await apiWrapper.post<any>(
		`${CIP_HANDLER_RAER_INVOKE_SERVICE}`,
		data,
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const createTaskUsingCIP = async (
	request: ICreateUpdateTask,
	citationId: string,
) => {
	const data = {
		citationId,
		source: 'rrcp-eTaskCreation',
		request: {
			taskRequestList: [request],
		},
	};
	const resp = await apiWrapper.post<any, IAPIErrorContent>(
		`${CIP_HANDLER_RAER_INVOKE_SERVICE}`,
		data,
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const updateTaskUsingCIP = async (
	request: ICreateUpdateTask,
	citationId: string,
) => {
	let source: string;
	if (request.status === 'Ready for Review') {
		source = 'rrcp-eTaskOwnerStatusUpdate';
	} else if (request.status === 'Complete') {
		source = 'rrcp-eComplianceManagerReview';
	} else {
		source = '';
	}
	const data = {
		citationId,
		source,
		request,
	};
	const resp = await apiWrapper.post<any, IAPIErrorContent>(
		`${CIP_HANDLER_RAER_INVOKE_SERVICE}`,
		data,
		CONTENT_TYPE.JSON,
		config.headers,
	);
	return resp;
};

export const getLatestDerivedObligation = async (
	params: IDerivedObligationRequestParams,
) => {
	const resp = await apiWrapper.get<IDerivedObligationSuccessResponse>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/latest-derived-obligation/${params.regulationName}`,
	);
	return resp;
};

export const updateDerivedObligation = async (
	data: IEditDerivedObligationRequestParams[],
) => {
	const resp = await apiWrapper.post<IEditDerivedObligationSuccessResponse[]>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/derived-obligation`,
		data,
	);
	return resp;
};

export const getEntityDetails = async (
	entityType: string,
	entityId: string,
) => {
	const resp = await apiWrapper.get<IGetEntityDetails>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/${entityType.toLowerCase()}/${entityId}/details`,
	);
	return resp;
};

export const getRegChangeEntityDetails = async (
	entityType: string,
	entityId: string,
	entityUpdateId: string,
) => {
	const resp = await apiWrapper.get<ICIPDetailsResponse | IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/${entityType}/${entityId}/details?citationUpdateId=${entityUpdateId}`,
	);
	return resp;
};

export const updateEntityStatus = async (
	entityType: string,
	citationId: string,
	data: IEntityDetailsStatus,
) => {
	const resp = await apiWrapper.post<any, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/${entityType}/${citationId}/status`,
		data,
	);
	return resp;
};

export const getRegulationTreeData = async (params: IRelationTreeInput) => {
	const resp = await apiWrapper.get<
		IRenderTreeSuccessResponse | IRenderTreeErrorResponse
	>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/regulations/${params.regulationId}`,
	);
	return resp;
};

export const updateEntityTag = async (
	entityId: string,
	entityType: string,
	tags: string[],
) => {
	const resp = await apiWrapper.put<IEntityServiceData, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/${entityType}/${entityId}`,
		{ tags },
	);
	return resp;
};

export const getEntityData = async (entityId: string, entityType: string) => {
	const resp = await apiWrapper.get<
		IEntityServiceData | IControlServiceData | IRiskServiceData
	>(`${REG_CHANGE_DOMAIN_SERVICE_URL}/${entityType.toLowerCase()}/${entityId}`);
	return resp;
};

export const getControlData = async (controlId: string) => {
	const resp = await apiWrapper.get<IControlServiceData>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/control/${controlId}`,
	);
	return resp;
};

export const getProcessData = async (processId: string) => {
	const resp = await apiWrapper.get<IProcessServiceData>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/process/${processId}`,
	);
	return resp;
};

export const getRelationshipTaskData = async (
	data: IGetRelationshipTaskData,
) => {
	const url = `${process.env.REACT_APP_REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/search`;
	const resp = await apiWrapper.post<IMappedTaskData, IAPIErrorContent>(
		url,
		data,
	);
	return resp;
};

export const getRiskData = async (riskId: string) => {
	const resp = await apiWrapper.get<IRiskServiceData, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/risk/${riskId}`,
	);
	return resp;
};

export const getGraphDetailsForNode = async (params: IGraphPanel) => {
	if (params.nodeType.toLowerCase() === ENTITY_TYPE.CITATION.toLowerCase()) {
		const resp = await apiWrapper.get<IGraphDetails>(
			`${REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/details/${params.nodeType}/${params.nodeId}`,
		);
		return resp;
	}
	const resp = await apiWrapper.get<IGraphDetails>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/${params.nodeType}/${params.nodeId}`,
	);
	return resp;
};

export const updateTask = async (
	data: ICreateUpdateTask | any,
	taskId: string,
) => {
	const resp = await apiWrapper.put<ICreateUpdateTask, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${taskId}`,
		data,
	);
	return resp;
};

export const getTaskComment = async (taskId: string) => {
	const resp = await apiWrapper.get<ITaskComment[]>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${taskId}/comment`,
	);
	return resp;
};

export const getRegInventComment = async (taskId: string, status: string) => {
	const resp = await apiWrapper.get<ITaskComment[]>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${taskId}/comment?taskStatus=${status}`,
	);
	return resp;
};
export const getCommentFileData = async (
	taskId: string,
	commentId: string,
	filename: string,
) => {
	const resp = await apiWrapper.get<string>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${taskId}/comment/${commentId}/attachment/${filename}`,
	);
	return resp;
};

export const saveCommentFileData = async (
	taskId: string,
	commentId: string,
	formData: FormData,
) => {
	const resp = await apiWrapper.post<ISaveComments>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${taskId}/comment/${commentId}/attachment`,
		formData,
		CONTENT_TYPE.MULTIPART,
	);
	return resp;
};

export const saveTaskComment = async (
	taskId: string,
	data: ITaskCreateComment,
) => {
	const resp = await apiWrapper.post<ITaskComment[], IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${taskId}/comment`,
		[data],
	);
	return resp;
};

export const updateTaskComment = async (
	taskId: string,
	commentId: string,
	data: ITaskComment,
) => {
	const resp = await apiWrapper.put<ITaskComment, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${taskId}/comment/${commentId}`,
		data,
	);
	return resp;
};

export const getImpactData = async (
	selectedId: string,
	parentObjectType: string,
	entityId: string,
	entityType: string,
) => {
	const resp = await apiWrapper.get<IImpactEntityData | IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/impact/${parentObjectType}/${entityId}/${entityType}/${selectedId}`,
	);
	return resp;
};

export const updateImpactForWorkflow = async (
	data: IImpactRequestParamForRegInv,
	impactId: string,
) => {
	const resp = await apiWrapper.put<IUpdateImpact>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/relationship/${impactId}`,
		data,
	);
	return resp;
};

export const getRemediationPlanTableData = async (citationId: string) => {
	const resp = await apiWrapper.get<IControlsResponse, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/citation/${citationId}/tasks`,
	);
	return resp;
};

export const getEntityTaskData = async (
	entityType: string,
	entityId: string,
) => {
	const resp = await apiWrapper.get<ITaskDetails[], IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${entityType}/${entityId}`,
	);
	return resp;
};

export const getFilteredGraphDataForCitationId = async (
	entityType: string,
	entityId: string,
) => {
	const resp = await apiWrapper.get<IGraph>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/${entityType}/${entityId}`,
	);
	return resp;
};

export const getRssNewsFeedData = async () => {
	const resp = await apiWrapper.get<IRSSNews | IRSSNewsErrorResponse>(
		`${REACT_APP_RSS_FEED_SERVICE_URL}/latest`,
	);
	return resp;
};

export const getCitations = async (params: ITaxonomyAddMappingParam) => {
	const resp = await apiWrapper.get<
		IAddRelationshipMappingMenu[] | IAddRelationMappingMenuErrorResponse
	>(`${REG_CHANGE_DOMAIN_SERVICE_URL}/citation/search`, params);
	return resp;
};

export const getProcesses = async (params: ITaxonomyAddMappingParam) => {
	const resp = await apiWrapper.get<
		IAddRelationshipMappingMenu[] | IAddRelationMappingMenuErrorResponse
	>(`${REG_CHANGE_DOMAIN_SERVICE_URL}/process/search`, params);
	return resp;
};

export const getControls = async (params: ITaxonomyAddMappingParam) => {
	const resp = await apiWrapper.get<
		IAddRelationshipMappingMenu[] | IAddRelationMappingMenuErrorResponse
	>(`${REG_CHANGE_DOMAIN_SERVICE_URL}/control/search`, params);
	return resp;
};
export const getPolicies = async (params: ITaxonomyAddMappingParam) => {
	params = { ...params };
	const resp = await apiWrapper.get<
		IAddRelationshipMappingMenu[] | IAddRelationMappingMenuErrorResponse
	>(`${REG_CHANGE_DOMAIN_SERVICE_URL}/policy/search`, params);
	return resp;
};

export const getRisks = async (params: ITaxonomyAddMappingParam) => {
	const resp = await apiWrapper.get<
		IAddRelationshipMappingMenu[] | IAddRelationMappingMenuErrorResponse
	>(`${REG_CHANGE_DOMAIN_SERVICE_URL}/risk/search`, params);
	return resp;
};

export const getObligations = async (params: IObligationAddMappingParam) => {
	const resp = await apiWrapper.get<
		IObligationResponse[] | IAddRelationMappingMenuErrorResponse
	>(`${REG_INV_URL}/group/search`, params);
	return resp;
};

export const getEntityRelationMapping = async (
	data: IEntityRelationInput,
	params?: IRelationMappingPayload,
) => {
	const resp = await apiWrapper.get<
		IGetRelationMappingDetail | IGetRelationMappingWithPagination
	>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/relationship/all?parentObjectId=${
			data.relationId
		}&parentObjectType=${data.relationType}&page=${params?.page || 0}&size=${
			params?.size || 12
		}&sort=${params?.sortKey || 'renderOrder'},${
			params?.sort || ''
		}&filterType=${params?.filterType || ''}`,
	);
	return resp;
};

export const submitDerivedObligations = async (
	assignedObjectId: string,
	data: IObligationsInp,
) => {
	const resp = await apiWrapper.put<IObligationsInp>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/task-manager/task/${assignedObjectId}/status`,
		data,
	);
	return resp;
};

export const getCoveredProductData = async (
	searchText?: string,
	source?: string,
) => {
	const resp = await apiWrapper.get<ICoveredProductRes[], IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/products?source=${source}&searchText=${
			searchText || ''
		}`,
	);
	return resp;
};

export const getRelationMapping = async (
	data: IEntityRelationInput,
	params: IGetRelationMappingParams,
) => {
	const resp = await apiWrapper.get<any>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/relationships?parentObjectId=${
			data.relationId
		}&parentObjectType=${data.relationType}&page=${
			params.pageNumber || 0
		}&size=${params.pageSize || 10}&sortField=${
			params.sortKey || ''
		}&sortOrder=${params.sortDirection || ''}`,
	);
	return resp;
};

export const getEntitiesCount = async (
	parentObjectId: string,
	parentObjectType: string,
) => {
	const resp = await apiWrapper.get<any, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/relation-mapping/entity/count?parentObjectId=${parentObjectId}&parentObjectType=${parentObjectType}`,
	);
	return resp;
};

export const getListOfMetaData = async (source: string, types: string) => {
	const resp = await apiWrapper.get<any, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/prc-inventory/details?source=${source}&keys=${types}`,
	);

	return resp;
};

export const submitPRCEditChanges = async (
	payloadData: ISubmitPRCEditChanges,
) => {
	const resp = await apiWrapper.post<any, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/prc-inventory/submit-changes`,
		payloadData,
	);
	return resp;
};

export const regenerateCards = async (payloadData: IRenegerateChanges) => {
	const resp = await apiWrapper.post<any, IAPIErrorContent>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/prc-inventory/generate/ai-cards`,
		payloadData,
	);
	return resp;
};

export const deleteAIGeneratedCards = async (
	parentId: string | undefined,
	data: IRenegerateChanges | undefined,
) => {
	const resp = await apiWrapper.delete<any>(
		`${REG_CHANGE_DOMAIN_SERVICE_URL}/prc-inventory/delete/ai-cards?parentId=${parentId}`,
		data,
	);
	return resp;
};
